/**
 * The 'keynote' theme — big-statement, stage-presentation drama.
 *
 * Design world: Apple-keynote energy. One idea per slide, lots of negative
 * space, minimal chrome, and a real DARK/LIGHT rhythm (`--xv-art-bg-inverse`
 * + `--xv-art-ink-inverse`) so a deck can alternate cinematic dark slides with
 * bright light ones. A single punchy electric-coral accent does all the
 * pointing; everything else is near-monochrome so the accent never competes.
 *
 * This is a COMPLETE theme: every required ArtTokenMap key, every §2.6 layout
 * token set to DISTINCT values, tokens for all 6 required treatments + the 4
 * optional ones, and a `layoutCss` block that pushes the geometry toward the
 * stage-presentation feel (oversized stat, huge display line, hairline rules,
 * wide eyebrow tracking, a quote that reads like a title card).
 *
 * Type: Space Grotesque (bold geometric/grotesque display, weight 700) paired
 * with Inter for body — clean, modern, no editorial serif. Tracking is pulled
 * very tight on display (-0.035em) for the dense, confident headline look.
 *
 * Layout language: 'keynote' — paged, generous gutter, big negative space.
 */
export const keynote = {
    id: 'keynote',
    label: 'Keynote',
    layoutLanguage: 'keynote',
    fontsUrl: 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap',
    tokens: {
        // ── 2.1 typography ──
        // Bold geometric grotesque for display; clean grotesque for body. The
        // huge keynote headline lives in Space Grotesk; prose stays in Inter so
        // long body copy reads calmly under the big titles.
        '--xv-art-font-display': "'Space Grotesk', system-ui, -apple-system, sans-serif",
        '--xv-art-font-body': "'Inter', system-ui, -apple-system, sans-serif",
        '--xv-art-font-mono': "'SFMono-Regular', ui-monospace, 'JetBrains Mono', Menlo, monospace",
        '--xv-art-font-weight-display': '700',
        '--xv-art-font-weight-body': '400',
        // Very tight display tracking — denser than Xavior's -0.02em — for that
        // confident, set-in-stone headline feel.
        '--xv-art-tracking-display': '-0.035em',
        // Wide eyebrow tracking reads as a stage cue / chapter marker.
        '--xv-art-tracking-eyebrow': '0.26em',
        // ── 2.2 surface ──
        // The light slide is pure paper-white (not tinted) so the dark slide reads
        // as the true contrast. Surface is barely-there grey — minimal chrome.
        '--xv-art-bg': '#ffffff',
        '--xv-art-bg-surface': '#f4f4f5',
        // The DARK slide — near-black, faintly warm so it photographs like a stage,
        // not a void. This is the whole point of the theme: decks alternate against it.
        '--xv-art-bg-inverse': '#0a0a0c',
        '--xv-art-surface-hover': '#ebebed',
        // ── 2.3 ink ──
        // Ink is near-black (not pure #000) for a slightly softer, premium black.
        '--xv-art-ink': '#0a0a0c',
        '--xv-art-ink-secondary': '#3f3f46',
        '--xv-art-ink-muted': '#a1a1aa',
        // Text on the dark slide — warm off-white, never harsh pure white.
        '--xv-art-ink-inverse': '#fafafa',
        // ── 2.4 accent ──
        // A single punchy electric-coral. It is the ONLY color in the theme; it
        // reads on both the white and the near-black slide. Used sparingly: the
        // accent square, the highlighted run, the eyebrow rule.
        '--xv-art-accent': '#ff3b30',
        '--xv-art-accent-ink': '#ffffff',
        '--xv-art-accent-dim': 'rgba(255, 59, 48, 0.10)',
        // Secondary accent for charts only — a cool slate so 2-series data reads
        // without introducing a competing brand color.
        '--xv-art-accent-2': '#3f3f46',
        // ── 2.5 borders ──
        // Hairlines only — keynote chrome is whisper-thin. Strong border is the
        // near-black ink itself for the rare full-weight rule under a headline.
        '--xv-art-border': '#e4e4e7',
        '--xv-art-border-strong': '#0a0a0c',
        // ── 2.6 layout — the structurally distinct scale ──
        // Narrow measure (one tight column of prose under big titles), a LARGE base
        // unit and an even larger gutter for cinematic negative space, near-square
        // corners (presentation surfaces, not cards), and a tight body line-height
        // with a very tight display line so multi-line titles stack like a headline.
        '--xv-art-measure': '680px',
        '--xv-art-space-unit': '12px',
        '--xv-art-gutter': '96px',
        '--xv-art-radius': '6px',
        '--xv-art-radius-sm': '3px',
        '--xv-art-line-height': '1.5',
        '--xv-art-line-height-display': '0.98',
        // ── 2.7 stat primitives ──
        // The hero stat is HUGE — this is the most cinematic number on the slide.
        // Tracks the resolved ink (#5339) so a brand kit's ink reaches the figure.
        '--xv-art-stat-ink': 'var(--xv-art-ink, #0a0a0c)',
        '--xv-art-stat-positive': '#34c759',
        '--xv-art-stat-negative': '#ff3b30',
        '--xv-art-stat-size': 'clamp(4rem, 12vw, 8.5rem)',
        // ── 3.1 highlight — inline emphasis ──
        // Color-only accent run, slightly heavier weight. No marker pill — keynote
        // emphasis is the word turning coral, nothing more. Tracks the resolved
        // accent so an applied brand kit's accent reaches inline emphasis (#5319).
        '--xv-art-highlight-ink': 'var(--xv-art-accent, #ff3b30)',
        '--xv-art-highlight-bg': 'transparent',
        '--xv-art-highlight-weight': '700',
        '--xv-art-highlight-pad': '0',
        // ── 3.2 callout — block emphasis panel ──
        // Minimal: a flat near-white panel, a coral insight bar, near-square corners.
        '--xv-art-callout-bg': '#f4f4f5',
        '--xv-art-callout-border': '#e4e4e7',
        // Callout body ink tracks the resolved ink (#5339).
        '--xv-art-callout-ink': 'var(--xv-art-ink, #0a0a0c)',
        '--xv-art-callout-radius': '6px',
        '--xv-art-callout-accent-insight': 'var(--xv-art-accent, #ff3b30)',
        '--xv-art-callout-accent-warning': '#ff9500',
        '--xv-art-callout-accent-success': '#34c759',
        // ── 3.3 stat ──
        // Uppercase, widely tracked labels under the giant value — the "142% NET
        // RETENTION" band. Numerals stay in the display grotesque for a unified
        // headline weight. Labels track the resolved muted ink (#5352).
        '--xv-art-stat-label-ink': 'var(--xv-art-ink-muted, #a1a1aa)',
        '--xv-art-stat-label-tracking': '0.14em',
        '--xv-art-stat-numeral-font': "'Space Grotesk', system-ui, sans-serif",
        // ── 3.4 quote — pull quote ──
        // The quote is a TITLE CARD: large, upright (not italic), display grotesque,
        // coral bar. It should read like a slide of its own. The title-card quote is
        // set in the FULL ink (not secondary) — it tracks the resolved ink (#5339).
        '--xv-art-quote-ink': 'var(--xv-art-ink, #0a0a0c)',
        '--xv-art-quote-bar': 'var(--xv-art-accent, #ff3b30)',
        '--xv-art-quote-font': "'Space Grotesk', system-ui, sans-serif",
        '--xv-art-quote-size': '2rem',
        '--xv-art-quote-style': 'normal',
        // ── 3.5 eyebrow — section kicker ──
        // The "04 — TRACTION" chapter marker. Muted, uppercase, very wide tracking,
        // coral leading rule. The only small type on a hero slide. Tracks the
        // resolved muted ink (#5352).
        '--xv-art-eyebrow-ink': 'var(--xv-art-ink-muted, #a1a1aa)',
        '--xv-art-eyebrow-tracking': '0.26em',
        '--xv-art-eyebrow-size': '0.7rem',
        '--xv-art-eyebrow-transform': 'uppercase',
        '--xv-art-eyebrow-rule': 'var(--xv-art-accent, #ff3b30)',
        // ── 3.6 wordmark — logo/brand slot ──
        // Footer wordmark: small, uppercase-feeling display grotesque, muted, with
        // a touch of tracking so it sits like a credit line. Tracks the resolved
        // muted ink (#5352).
        '--xv-art-wordmark-ink': 'var(--xv-art-ink-muted, #a1a1aa)',
        '--xv-art-wordmark-font': "'Space Grotesk', system-ui, sans-serif",
        '--xv-art-wordmark-size': '0.85rem',
        '--xv-art-wordmark-weight': '600',
        // ── 3.7 optional treatments ──
        '--xv-art-link-ink': 'var(--xv-art-accent, #ff3b30)',
        '--xv-art-code-bg': '#f4f4f5',
        // Code ink tracks the resolved ink (#5339).
        '--xv-art-code-ink': 'var(--xv-art-ink, #0a0a0c)',
        // ── 4.2 logo slot (theme leaves logo empty → text wordmark renders) ──
        '--xv-art-logo-url': 'none',
        '--xv-art-logo-aspect': 'auto',
        '--xv-art-logo-height': '0.85rem',
        '--xv-art-logo-invert': '0',
    },
    /**
     * Richer per-treatment geometry for the stage-presentation feel. Scoped under
     * [data-xv-theme="keynote"]. The token-only path above already renders a
     * coherent result; this layer pushes the drama: massive headline spacing, a
     * coral eyebrow tick, an oversized hanging quote, a hairline stat band, and an
     * accent square that sits beside the giant stat. Email renderers that read
     * only token VALUES still get the full high-contrast look without this CSS.
     */
    layoutCss: `
[data-xv-theme="keynote"] {
  letter-spacing: -0.01em;
}

/* Headlines: tight, dense, one-idea-per-slide. Big top margin = negative space. */
[data-xv-theme="keynote"] h1,
[data-xv-theme="keynote"] .xv-art-headline {
  font-family: var(--xv-art-font-display);
  font-weight: var(--xv-art-font-weight-display);
  letter-spacing: var(--xv-art-tracking-display);
  line-height: var(--xv-art-line-height-display);
  font-size: clamp(2.75rem, 7vw, 5.25rem);
  text-wrap: balance;
}
[data-xv-theme="keynote"] h2 {
  font-family: var(--xv-art-font-display);
  font-weight: 600;
  letter-spacing: var(--xv-art-tracking-display);
  line-height: 1.05;
  font-size: clamp(1.75rem, 3.5vw, 2.75rem);
}

/* Eyebrow: a coral tick + the wide-tracked chapter marker. */
[data-xv-theme="keynote"] .xv-art-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: calc(var(--xv-art-space-unit) * 1.25);
  font-family: var(--xv-art-font-display);
  font-weight: 600;
}
[data-xv-theme="keynote"] .xv-art-eyebrow::before {
  content: '';
  width: calc(var(--xv-art-space-unit) * 2.5);
  height: 2px;
  background: var(--xv-art-eyebrow-rule);
  flex: none;
}

/* The hero stat: enormous, ultra-tight, with the accent square hard beside it. */
[data-xv-theme="keynote"] .xv-art-stat {
  font-family: var(--xv-art-font-display);
  font-weight: var(--xv-art-font-weight-display);
  letter-spacing: -0.045em;
  line-height: 0.92;
  font-variant-numeric: tabular-nums;
}
[data-xv-theme="keynote"] .xv-art-stat__label {
  font-family: var(--xv-art-font-body);
  font-weight: 600;
  text-transform: uppercase;
}

/* The two-stat band: hairline-separated, lots of air between figures. */
[data-xv-theme="keynote"] .xv-art-stats-band {
  display: flex;
  gap: calc(var(--xv-art-gutter) * 0.75);
  border-top: 1px solid var(--xv-art-border);
  padding-top: calc(var(--xv-art-space-unit) * 2.5);
}

/* Highlight: the word simply turns coral — no pill, no underline. */
[data-xv-theme="keynote"] .xv-art-highlight {
  color: var(--xv-art-highlight-ink);
  font-weight: var(--xv-art-highlight-weight);
}

/* Pull quote as a title card: oversized, upright, hanging coral bar. */
[data-xv-theme="keynote"] .xv-art-quote {
  font-family: var(--xv-art-quote-font);
  font-weight: 500;
  font-size: var(--xv-art-quote-size);
  font-style: var(--xv-art-quote-style);
  letter-spacing: -0.025em;
  line-height: 1.1;
  color: var(--xv-art-quote-ink);
  border-left: 3px solid var(--xv-art-quote-bar);
  padding-left: calc(var(--xv-art-space-unit) * 2.5);
  text-wrap: balance;
}

/* Callout: flat panel, thick coral insight bar, minimal chrome. */
[data-xv-theme="keynote"] .xv-art-callout {
  border-radius: var(--xv-art-callout-radius);
  border-left-width: 3px;
  padding: calc(var(--xv-art-space-unit) * 2);
}

/* Wordmark: a quiet credit line at the foot of the slide. */
[data-xv-theme="keynote"] .xv-art-wordmark {
  font-family: var(--xv-art-wordmark-font);
  font-weight: var(--xv-art-wordmark-weight);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--xv-art-wordmark-ink);
}

/* Dark-slide rhythm: when a slide opts into the inverse canvas, ink + chrome
   flip so the same blocks read on near-black. The accent stays coral. */
[data-xv-theme="keynote"] .xv-art--inverse,
[data-xv-theme="keynote"] [data-xv-inverse="true"] {
  background: var(--xv-art-bg-inverse);
  color: var(--xv-art-ink-inverse);
}
[data-xv-theme="keynote"] .xv-art--inverse .xv-art-stat,
[data-xv-theme="keynote"] [data-xv-inverse="true"] .xv-art-stat {
  color: var(--xv-art-ink-inverse);
}
[data-xv-theme="keynote"] .xv-art--inverse .xv-art-border,
[data-xv-theme="keynote"] [data-xv-inverse="true"] .xv-art-stats-band {
  border-color: rgba(250, 250, 250, 0.18);
}
`,
};
//# sourceMappingURL=keynote.js.map